import React, { useCallback, useEffect, useState } from 'react';

const RECENT_KEY = 'bopppsRecentColors';
const MAX_RECENT = 8;

export const DEFAULT_BACKGROUND_SWATCHES = [
    '#545f7d', '#1e293b', '#0f172a', '#7c3aed', '#1d4ed8', '#0e7490',
    '#047857', '#b91c1c', '#c2410c', '#a16207', '#f8fafc', '#ffffff'
];

export const DEFAULT_TEXT_SWATCHES = [
    '#ffffff', '#f8fafc', '#e2e8f0', '#fde68a', '#a7f3d0', '#bfdbfe',
    '#1f2937', '#0f172a'
];

const readRecents = () => {
    try {
        const raw = window.localStorage.getItem(RECENT_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed.filter((c) => /^#[0-9a-fA-F]{6}$/.test(c)) : [];
    } catch {
        return [];
    }
};

const normalizeHex = (value) => {
    if (typeof value !== 'string') return null;
    let hex = value.trim();
    if (/^[0-9a-fA-F]{6}$/.test(hex)) hex = `#${hex}`;
    if (/^#[0-9a-fA-F]{3}$/.test(hex)) {
        hex = `#${hex.slice(1).split('').map((c) => c + c).join('')}`;
    }
    return /^#[0-9a-fA-F]{6}$/.test(hex) ? hex.toLowerCase() : null;
};

/**
 * Modern color control: curated swatch grid, recently used colors, and a
 * custom picker with hex input. Recents persist across sessions.
 */
const ColorSwatchPicker = ({ label, value, onChange, swatches = DEFAULT_BACKGROUND_SWATCHES }) => {
    const [recents, setRecents] = useState(readRecents);
    const [hexDraft, setHexDraft] = useState(value || '');

    useEffect(() => { setHexDraft(value || ''); }, [value]);

    const rememberColor = useCallback((color) => {
        const hex = normalizeHex(color);
        if (!hex) return;
        setRecents((prev) => {
            const next = [hex, ...prev.filter((c) => c !== hex)].slice(0, MAX_RECENT);
            try { window.localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* ignore */ }
            return next;
        });
    }, []);

    const pick = useCallback((color) => {
        const hex = normalizeHex(color);
        if (!hex) return;
        onChange(hex);
        rememberColor(hex);
    }, [onChange, rememberColor]);

    const commitHexDraft = () => {
        const hex = normalizeHex(hexDraft);
        if (hex) pick(hex);
        else setHexDraft(value || '');
    };

    const current = normalizeHex(value);

    const renderSwatch = (color) => {
        const hex = normalizeHex(color);
        const isActive = hex === current;
        return (
            <button
                key={color}
                type="button"
                onClick={() => pick(color)}
                className={`relative h-9 w-9 rounded-lg border transition-transform hover:scale-110 ${
                    isActive ? 'border-blue-600 ring-2 ring-blue-300' : 'border-gray-300'
                }`}
                style={{ backgroundColor: color }}
                title={color}
            >
                {isActive && (
                    <span
                        className="absolute inset-0 flex items-center justify-center text-xs font-bold"
                        style={{ color: parseInt(hex.slice(1), 16) > 0x888888 ? '#1f2937' : '#ffffff' }}
                    >
                        ✓
                    </span>
                )}
            </button>
        );
    };

    return (
        <div>
            {label && <label className="block text-sm font-semibold text-gray-700 mb-2">{label}</label>}
            <div className="rounded-xl border border-gray-200 p-3 space-y-3 bg-gray-50">
                <div className="flex flex-wrap gap-2">
                    {swatches.map(renderSwatch)}
                </div>
                {recents.length > 0 && (
                    <div>
                        <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Recent</p>
                        <div className="flex flex-wrap gap-2">
                            {recents.map(renderSwatch)}
                        </div>
                    </div>
                )}
                <div className="flex items-center gap-2 pt-1 border-t border-gray-200">
                    <input
                        type="color"
                        value={current || '#ffffff'}
                        onChange={(e) => pick(e.target.value)}
                        className="h-9 w-12 rounded-lg border border-gray-300 cursor-pointer"
                        title="Custom color"
                    />
                    <input
                        type="text"
                        value={hexDraft}
                        onChange={(e) => setHexDraft(e.target.value)}
                        onBlur={commitHexDraft}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitHexDraft(); } }}
                        className="w-28 px-2 py-1.5 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="#545f7d"
                    />
                    <span
                        className="flex-1 h-9 rounded-lg border border-gray-200"
                        style={{ backgroundColor: current || '#ffffff' }}
                        aria-hidden="true"
                    />
                </div>
            </div>
        </div>
    );
};

export default ColorSwatchPicker;
